<template>
    <div id="pipeline">
        <!--entry information-->
        <portuna-box title="Informationen" title-icon="info">
            <!--application & enrolment-->
            <div class="enrolment-segment">
                <div class="row">
                    <div class="col-6">
                        <div class="spacer-8"/>
                        <person-box
                            :image="isAllowed && application.portrait && application.portrait.paths && application.portrait.paths.preview || ''"
                            :name="application.name + ' ' + application.surname"
                            class="medium application-person-box">
                            <template #name>
                                <router-link v-if="isAllowed" :to="{name: 'enrolment-application-view', params: {applicationId: application.id}}"
                                    class="font-weight-500">
                                    {{fullName}} (ID: {{application.id}})
                                </router-link>
                                <template v-else>
                                    {{fullName}} (ID: {{application.id}})
                                </template>
                            </template>

                            <template v-if="isAllowed" #default>
                                <a v-if="application.email" :href="'mailto:' + application.email">
                                    {{application.email}} <i aria-hidden="true" class="material-icons md-12 color-primary" role="img">email</i>
                                </a>
                                <span v-else>
                                    <em>Keine Angabe</em>
                                    <i aria-hidden="true" class="material-icons md-12 color-primary" role="img">email</i>
                                </span>
                            </template>
                        </person-box>
                    </div>

                    <div class="col-6">
                        <strong>Beworben für die Stelle:</strong>
                        <div>
                            <router-link :to="{name: 'enrolment-view', params: {id: enrolment.id}}">
                                {{enrolment.description}} <i aria-hidden="true" class="material-icons md-12" role="img">open_in_new</i>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>

            <hr class="enrolment-segment"/>

            <!--appointment data-->
            <div class="enrolment-segment">
                <div class="row">
                    <div class="col-6">
                        <strong>Terminbeginn</strong>
                        <br/>
                        {{entry.date_start}}, {{entry.time_start}} Uhr
                    </div>

                    <div class="col-6">
                        <strong>Terminende</strong>
                        <br/>
                        {{entry.date_end}}, {{entry.time_end}} Uhr
                    </div>
                </div>
                <div class="row spacer-15">
                    <div class="col-6">
                        <strong>Typ</strong>
                        <br/>
                        <template v-if="entry.type === appointmentTypes.online">Online</template>
                        <template v-else-if="entry.room_id === appointmentLocationTypes.internal">Interner Ort</template>
                        <template v-else-if="entry.room_id === appointmentLocationTypes.external">Externer Ort</template>
                        <template v-else>Ort</template>

                    </div>

                    <div v-if="entry.address_description" class="col-6 hyphens-auto">
                        <strong>Standort</strong>
                        <br/>
                        {{entry.address_description}}
                        <div class="spacer spacer-15">&nbsp;</div>
                    </div>

                    <div class="col-6 hyphens-auto">
                        <strong>Lokalität</strong>
                        <br/>
                        <template v-if="entry.room_title">{{entry.room_title}}</template>
                        <template v-else-if="entry.room_location_description">{{entry.room_location_description}}</template>
                        <template v-else-if="entry.online_location_description">{{entry.online_location_description}}</template>
                    </div>
                </div>
            </div>

            <div v-if="entry.description" class="enrolment-segment">
                <strong>Terminbeschreibung</strong>
                <div class="hyphens-auto">
                    {{entry.description}}
                </div>
            </div>
        </portuna-box>

        <portuna-box title="Terminteilnehmer" title-icon="info">
            <div class="row">
                <div class="col-6">
                    <strong>Entscheider</strong>&nbsp;
                    <div class="spacer-10"/>
                    <template v-if="entry.main_contacts && entry.main_contacts.length">
                        <person-box-employee v-for="employeeId in entry.main_contacts" :id="employeeId" :key="'employee-' + employeeId"/>
                    </template>
                    <div v-else>
                        Keine Entscheider vorhanden.
                    </div>
                </div>

                <div class="col-6">
                    <strong>Zusätzliche Verantwortliche</strong>&nbsp
                    <div class="spacer-10"/>
                    <template v-if="entry.assignees && entry.assignees.length">
                        <person-box-employee v-for="employeeId in entry.assignees" :id="employeeId" :key="'employee-' + employeeId"/>
                    </template>
                    <div v-else>
                        Keine Verantwortliche vorhanden.
                    </div>
                </div>
            </div>
        </portuna-box>
    </div>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import PersonBox from '../../../../components/PersonBox.vue';
    import PersonBoxEmployee from '../../../../components/PersonBoxEmployee.vue';
    import PortunaBox from '../../../../components/PortunaBox.vue';
    import {appointmentLocationTypes, appointmentTypes} from '@/library/internalExternalAppointment';

    const props    = defineProps<{
        entry: ApplicationAppointmentEntry;
        application: ApplicationEntry;
        enrolment: EnrolmentEntry;
        isAllowed: boolean;
    }>();

    const fullName = computed((): string =>
    {
        return [
            props.application.salutation,
            props.application.title,
            props.application.name,
            props.application.surname,
        ]
        .filter((v) => v)
        .join(' ');
    });

</script>